import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import ConfirmDialog from '../../../../components/dialogs/ConfirmDialog';
import ErrorDialog from '../../../../components/dialogs/ErrorDialog';
import type { AegOccurrenceFilesController } from '../../../../hooks/useAegOccurrenceFiles';
import { getUserProfile } from '../../../../configuration/login/loginSlice';
import { formatDateTimeWithTimezone } from '../../../../utils/dateUtils';
import { formatFileSize } from '../../../../utils/imageSizeUtils';
import './styles.css';

type AttachmentsProps = AegOccurrenceFilesController & {
    disabled?: boolean;
};

const getExtension = (name: string) => (name.includes('.') ? (name.split('.').pop() ?? '') : '');

export default function Attachments({
    disabled,
    files,
    loading,
    uploading,
    error,
    fileToDelete,
    uploadFiles,
    download,
    removeFile,
    setFileToDelete,
    clearError,
}: AttachmentsProps) {
    const intl = useIntl();
    const t = (id: string, values?: Record<string, string>) =>
        intl.formatMessage({ id: `intl-msg:${id}` }, values);
    const userProfile = useSelector(getUserProfile);
    const userId = userProfile?.sub?.split(':')?.[1];

    return (
        <div className='aeg-attachments'>
            <div className='aeg-attachments-head'>
                <h3 className='aeg-attachments-title margin-0 text-bold text-color-darkest'>{t('support.upload.uploadedFiles')}</h3>
                {!disabled && (
                    <label
                        className={`aeg-attachments-upload btn btn-primary ${uploading ? 'btn-loading disabled' : ''}`}
                    >
                        <span aria-hidden='true' className='rioglyph rioglyph-plus' />
                        {t('tas.support.upload.newFile')}
                        <input
                            multiple
                            onChange={event => {
                                void uploadFiles(Array.from(event.target.files ?? []));
                                event.target.value = '';
                            }}
                            className='aeg-attachments-input'
                            type='file'
                        />
                    </label>
                )}
            </div>
            <ul className='aeg-attachments-list'>
                {loading ? (
                    <li className='aeg-attachments-empty padding-30 text-center text-color-dark'>
                        {t('support.aeg.detail.attachments.loading')}
                    </li>
                ) : files.length ? (
                    files.map(file => {
                        const canDelete = !disabled && (file.availableToDelete ?? userId === file.uploadedById);

                        return (
                            <li
                                className='aeg-attachments-item'
                                key={file.fileId}
                            >
                                <span className='aeg-attachments-ext'>{getExtension(file.name)}</span>
                                <div className='aeg-attachments-info'>
                                    <span className='aeg-attachments-name text-color-darkest'>
                                        {file.name}
                                    </span>
                                    <small className='aeg-attachments-meta text-color-dark'>
                                        {t('support.aeg.detail.attachments.meta', {
                                            size: formatFileSize(file.fileSize),
                                            by: file.uploadedBy ?? '—',
                                            at: file.uploadedAt ? formatDateTimeWithTimezone(file.uploadedAt) : '—',
                                        })}
                                    </small>
                                </div>
                                <button className='btn btn-default' onClick={() => void download(file)} type='button'>
                                    {t('support.aeg.detail.attachments.download')}
                                </button>
                                {canDelete && (
                                    <button
                                        aria-label={t('support.aeg.detail.attachments.remove', { name: file.name })}
                                        className='aeg-attachments-icon-button'
                                        onClick={() => setFileToDelete(file)}
                                        type='button'
                                    >
                                        <span className='rioglyph rioglyph-trash' />
                                    </button>
                                )}
                            </li>
                        );
                    })
                ) : (
                    <li className='aeg-attachments-empty padding-30 border border-style-dashed rounded text-center text-color-dark'>
                        {t('support.aeg.detail.attachments.noFiles')}
                    </li>
                )}
            </ul>
            <ConfirmDialog
                body={t('support.simpleUpload.removeFileBody')}
                onClose={() => setFileToDelete(null)}
                onConfirm={() => void removeFile()}
                show={!!fileToDelete}
                title={t('support.simpleUpload.removeFileTitle')}
            />
            <ErrorDialog
                body={error ? t(error.id, error.values) : ''}
                onClose={clearError}
                show={!!error}
                title={t('tas.support.upload.errorTitle')}
            />
        </div>
    );
}
